import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { MapPin, User, CalendarDays, CreditCard, Tent, Caravan, Truck, Star, Send, Droplets, Zap, Plug, Wifi, ShowerHead, Flame, Bath, PawPrint, Moon, Trees, MessageCircle, Clock, Award, CheckCircle2, AlertTriangle, Flag } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import MapComponent from "@/components/MapComponent";
import FavoriteButton from "@/components/FavoriteButton";
import ReportDialog from "@/components/ReportDialog";
import { useSiteSettings } from "@/contexts/SettingsContext";

const DEFAULT_IMAGES = ["/images/hero.jpg"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TERRAIN_LABELS = { rv: "VR / Motorisé", tent: "Tente", van: "Van aménagé", trailer: "Roulotte / Caravane", other: "Autre" };
const LENGTH_LABELS = { "10-15": "10 à 15 pieds", "16-25": "16 à 25 pieds", "26-35": "26 à 35 pieds", "36-45": "36 à 45 pieds", "45+": "45 pieds et plus" };
const TERRAIN_ICONS = { rv: Caravan, tent: Tent, van: Truck, trailer: Caravan, other: Tent };
const SURFACE_LABELS = { grass: "Gazon", gravel: "Gravier", asphalt: "Asphalte", dirt: "Terre" };
const AMENITY_LIST = [
  { key: "water", label: "Eau", icon: Droplets, color: "text-blue-500" },
  { key: "electricity_15a", label: "Électricité 15A", icon: Plug, color: "text-yellow-500" },
  { key: "electricity_30a", label: "Électricité 30A", icon: Zap, color: "text-amber-500" },
  { key: "electricity_50a", label: "Électricité 50A", icon: Zap, color: "text-orange-500" },
  { key: "ev_charger", label: "Borne de recharge VE", icon: Zap, color: "text-emerald-500" },
  { key: "sewer", label: "Egout", icon: Bath, color: "text-zinc-500" },
  { key: "toilet", label: "Toilette", icon: Bath, color: "text-blue-400" },
  { key: "shower", label: "Douche", icon: ShowerHead, color: "text-cyan-500" },
  { key: "wifi", label: "WiFi", icon: Wifi, color: "text-violet-500" },
  { key: "fire_pit", label: "Feu de camp", icon: Flame, color: "text-orange-500" },
];

function getImageUrl(s, fallback = 0) {
  const img = s.photos?.[0] || s.image_url;
  if (!img) return DEFAULT_IMAGES[fallback];
  if (img.startsWith("/api/")) return `${BACKEND_URL}${img}`;
  return img;
}

function StarRating({ rating, onRate, interactive = false, size = "h-5 w-5" }) {
  return (<div className="flex gap-0.5">{[1,2,3,4,5].map(i => (<Star key={i} className={`${size} ${i <= rating ? "fill-amber-400 text-amber-400" : "text-zinc-300"} ${interactive ? "cursor-pointer hover:text-amber-400" : ""}`} onClick={interactive ? () => onRate(i) : undefined} data-testid={interactive ? `rate-star-${i}` : undefined} />))}</div>);
}

export default function SpaceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useSiteSettings();
  const appName = settings?.app_name || "VroumZy";
  const cancellationSummary = settings?.cancellation_summary || "Annulation gratuite à plus de 7 jours · 30 % de frais entre 7 j et 48 h · annulation impossible à moins de 48 h.";

  // Booking disclaimer (admin can override each part from /settings)
  const disclaimerTitle = (settings?.booking_disclaimer_title || "").trim() || "Conditions de réservation";
  const disclaimerIntro = (settings?.booking_disclaimer_intro || "").trim();
  const disclaimerBulletsRaw = (settings?.booking_disclaimer_bullets || "").trim();
  const disclaimerBullets = disclaimerBulletsRaw
    ? disclaimerBulletsRaw.split("\n").map(s => s.trim()).filter(Boolean)
    : [
        "Je suis seul(e) responsable du respect des règles de l'hôte, des lois municipales, provinciales et fédérales applicables (camping, feu, bruit, faune, déchets, alcool, etc.).",
        `**${appName} n'est pas responsable** des dommages, infractions, amendes ou litiges découlant du non-respect de ces règles ou lois.`,
        "Il est de mon devoir, ainsi que de celui de toute personne m'accompagnant, de ne commettre aucune infraction sur le terrain ou aux alentours.",
        "L'hôte peut annuler ma réservation sans remboursement si je ne respecte pas ses règles.",
      ];
  const disclaimerAcceptLabel = (settings?.booking_disclaimer_accept_label || "").trim()
    || `J'accepte ces conditions et je dégage ${appName}.com de toute responsabilité.`;

  // Render a single bullet supporting **bold** markdown
  const renderBullet = (text, key) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return (
      <li key={key}>
        {parts.map((p, i) =>
          p.startsWith("**") && p.endsWith("**")
            ? <strong key={i}>{p.slice(2, -2)}</strong>
            : <span key={i}>{p}</span>
        )}
      </li>
    );
  };
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(undefined);
  const [numSpots, setNumSpots] = useState(1);
  const [booking, setBooking] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [newRating, setNewRating] = useState(0);
  const [newComment, setNewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [contactMsg, setContactMsg] = useState("");
  const [sendingContact, setSendingContact] = useState(false);
  const [requestMsg, setRequestMsg] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [canReview, setCanReview] = useState({ can_review: false, has_completed_stay: false, already_reviewed: false });
  const [blockedDates, setBlockedDates] = useState([]);
  const [existingBookings, setExistingBookings] = useState([]);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    api.get(`/spaces/${id}`).then(r => setSpace(r.data)).catch(() => toast.error("Terrain non trouvé")).finally(() => setLoading(false));
    api.get(`/spaces/${id}/reviews`).then(r => setReviews(r.data)).catch(() => {});
    api.get(`/spaces/${id}/blocked-dates`).then(r => setBlockedDates(r.data || [])).catch(() => {});
    api.get(`/spaces/${id}/availability`).then(r => setExistingBookings(r.data || [])).catch(() => {});
    if (user) api.get(`/spaces/${id}/can-review`).then(r => setCanReview(r.data)).catch(() => {});
  }, [id, user]);

  const nights = dateRange?.from && dateRange?.to ? Math.ceil((dateRange.to - dateRange.from) / (1000*60*60*24)) : 0;

  // Compute disabled (host-blocked) and fully-booked date arrays for calendar display
  const { blockedDateObjs, bookedDateObjs } = (() => {
    const blocked = [];
    const booked = [];
    const maxSpots = Math.max(1, parseInt(space?.max_spots || 1));
    const requested = Math.max(1, parseInt(numSpots || 1));

    // Host-blocked ranges = always disabled
    (blockedDates || []).forEach(b => {
      try {
        const start = new Date(b.start_date);
        const end = new Date(b.end_date);
        for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
          blocked.push(new Date(d));
        }
      } catch { /* ignore */ }
    });

    // For each night within booking horizon, check capacity vs requested
    const horizonStart = new Date();
    horizonStart.setHours(0, 0, 0, 0);
    const horizonEnd = new Date(horizonStart);
    horizonEnd.setFullYear(horizonEnd.getFullYear() + 2);

    for (let cursor = new Date(horizonStart); cursor < horizonEnd; cursor.setDate(cursor.getDate() + 1)) {
      let used = 0;
      for (const b of existingBookings || []) {
        try {
          const ci = new Date(b.check_in);
          const co = new Date(b.check_out);
          if (cursor >= new Date(ci.getFullYear(), ci.getMonth(), ci.getDate()) &&
              cursor < new Date(co.getFullYear(), co.getMonth(), co.getDate())) {
            used += parseInt(b.num_spots || 1) || 1;
          }
        } catch { /* ignore */ }
      }
      if (used + requested > maxSpots) {
        booked.push(new Date(cursor));
      }
    }
    return { blockedDateObjs: blocked, bookedDateObjs: booked };
  })();

  const calendarModifiers = { blocked: blockedDateObjs, booked: bookedDateObjs };
  const calendarModifiersStyles = {
    blocked: { backgroundColor: "#fecaca", color: "#991b1b", borderRadius: "4px" },
    booked: { backgroundColor: "#fed7aa", color: "#9a3412", borderRadius: "4px", textDecoration: "line-through" },
  };
  const calendarDisabled = [
    { before: new Date() },
    ...blockedDateObjs,
    ...bookedDateObjs,
  ];

  const calcPrice = () => {
    if (!space || nights <= 0) return { base: 0, fee: 0, total: 0 };
    const ppn = space.price_per_night || 0;
    const w = space.weekly_price;
    const m = space.monthly_price;
    let base;
    if (m && nights >= 28) base = (m / 28) * nights * numSpots;
    else if (w && nights >= 7) base = (w / 7) * nights * numSpots;
    else base = ppn * nights * numSpots;
    base = Math.round(base * 100) / 100;
    const fee = Math.round(base * 0.03 * 100) / 100;
    return { base, fee, total: Math.round((base + fee) * 100) / 100 };
  };
  const pricing = calcPrice();
  const totalPrice = pricing.total.toFixed(2);

  const handleBook = async () => {
    if (!user) { navigate("/login"); return; }
    if (!dateRange?.from || !dateRange?.to || nights <= 0) { toast.error("Sélectionnez des dates valides"); return; }
    if (!acceptedTerms) { toast.error("Vous devez accepter les conditions de réservation pour continuer."); return; }
    setBooking(true);
    try {
      await api.post("/bookings", {
        space_id: id, check_in: dateRange.from.toISOString(), check_out: dateRange.to.toISOString(),
        num_spots: numSpots, message: requestMsg, accepted_terms: true,
      });
      toast.success("Demande envoyée au propriétaire ! Vous serez notifié de sa réponse.");
      navigate("/my-bookings");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setBooking(false); }
  };

  const handleReview = async () => {
    if (!user) { navigate("/login"); return; }
    if (newRating === 0) { toast.error("Donnez une note"); return; }
    setSubmittingReview(true);
    try {
      const { data } = await api.post(`/spaces/${id}/reviews`, { rating: newRating, comment: newComment });
      setReviews(prev => [data, ...prev]);
      setNewRating(0); setNewComment("");
      toast.success("Avis publie!");
      const updated = await api.get(`/spaces/${id}`);
      setSpace(updated.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSubmittingReview(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-zinc-300 border-t-emerald-700 rounded-full" /></div>;
  if (!space) return <div className="max-w-7xl mx-auto px-4 py-20 text-center"><p className="text-zinc-500">Terrain non trouvé</p></div>;

  const TIcon = TERRAIN_ICONS[space.terrain_type] || Tent;
  const photos = space.photos?.length ? space.photos : [getImageUrl(space)];
  const amenities = space.amenities || {};
  const rules = space.rules || {};
  const activeAmenities = AMENITY_LIST.filter(a => amenities[a.key]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Photos */}
          <div className="rounded-2xl overflow-hidden aspect-video relative">
            <img src={photos[activePhoto]?.startsWith("/api/") ? `${BACKEND_URL}${photos[activePhoto]}` : (photos[activePhoto] || DEFAULT_IMAGES[0])} alt={space.title} className="w-full h-full object-cover" />
            <div className="absolute top-3 right-3">
              <FavoriteButton spaceId={space.id} />
            </div>
          </div>
          {photos.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-2">
              {photos.map((p, i) => (
                <button key={p || i} onClick={() => setActivePhoto(i)} className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-colors ${i === activePhoto ? "border-emerald-600" : "border-transparent"}`} data-testid={`photo-thumb-${i}`}>
                  <img src={p.startsWith("/api/") ? `${BACKEND_URL}${p}` : p} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div>
            <div className="flex items-start justify-between gap-4 mb-2">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950" data-testid="space-title">{space.title}</h1>
              {space.is_free ? (
                <span className="text-2xl font-bold text-amber-600 whitespace-nowrap" data-testid="space-price">Gratuit</span>
              ) : (
                <span className="text-2xl font-bold text-emerald-600 whitespace-nowrap" data-testid="space-price">{space.price_per_night?.toFixed(2)} $/nuit</span>
              )}
            </div>
            <p className="text-zinc-500 flex items-center gap-1 mb-3"><MapPin className="h-4 w-4" /> {space.address}, {space.city}{space.region ? `, ${space.region}` : ""}</p>
            {user && user.id !== space.owner_id && (
              <button
                type="button"
                onClick={() => setReportOpen(true)}
                className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-red-600 mb-3 transition-colors"
                data-testid="report-space-btn"
              >
                <Flag className="h-3 w-3" /> Signaler ce terrain
              </button>
            )}
            <div className="flex flex-wrap gap-2 mb-4">
              {(() => {
                const types = (space.terrain_types && space.terrain_types.length > 0) ? space.terrain_types : [space.terrain_type];
                return types.map((t) => {
                  const I = TERRAIN_ICONS[t] || Tent;
                  return (
                    <Badge key={t} variant="outline" className="rounded-lg" data-testid={`terrain-type-badge-${t}`}>
                      <I className="h-3 w-3 mr-1" /> {TERRAIN_LABELS[t] || t}
                    </Badge>
                  );
                });
              })()}
              {space.max_vehicle_length && LENGTH_LABELS[space.max_vehicle_length] && (
                <Badge variant="outline" className="rounded-lg bg-amber-50 text-amber-700 border-amber-200" data-testid="max-vehicle-length-badge">
                  <Truck className="h-3 w-3 mr-1" /> Jusqu&apos;à {LENGTH_LABELS[space.max_vehicle_length]}
                </Badge>
              )}
              <Badge variant="outline" className="rounded-lg bg-emerald-50 text-emerald-700 border-emerald-200">Disponible</Badge>
              <Badge variant="outline" className="rounded-lg">{SURFACE_LABELS[space.surface_type] || space.surface_type}</Badge>
              <Badge variant="outline" className="rounded-lg"><Trees className="h-3 w-3 mr-1" /> {space.max_spots || 1} emplacement(s)</Badge>
              {space.avg_rating > 0 && <Badge variant="outline" className="rounded-lg"><Star className="h-3 w-3 fill-amber-400 text-amber-400 mr-1" /> {space.avg_rating} ({space.review_count})</Badge>}
            </div>
            {/* Check-in / Check-out times */}
            {(space.check_in_time || space.check_out_time) && (
              <div className="flex gap-4 mb-4 text-sm text-zinc-600">
                {space.check_in_time && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-100">
                    <Clock className="h-3.5 w-3.5 text-emerald-600" />
                    <span>Arrivée : <strong className="text-emerald-700">{space.check_in_time}</strong></span>
                  </div>
                )}
                {space.check_out_time && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-100 border border-zinc-200">
                    <Clock className="h-3.5 w-3.5 text-zinc-500" />
                    <span>Départ : <strong className="text-zinc-700">{space.check_out_time}</strong></span>
                  </div>
                )}
              </div>
            )}
            {space.weekly_price && <p className="text-sm text-zinc-600 mb-1">Tarif semaine: <strong>{space.weekly_price.toFixed(2)} $/semaine</strong></p>}
            {space.monthly_price && <p className="text-sm text-zinc-600 mb-2">Tarif mois: <strong>{space.monthly_price.toFixed(2)} $/mois</strong></p>}
            <Separator className="mb-4" />
            <p className="text-zinc-600 leading-relaxed" data-testid="space-description">{space.description}</p>
            <div className="mt-4 flex items-center gap-2 text-sm text-zinc-500 flex-wrap"><User className="h-4 w-4" /><span>Publie par {space.owner_id ? (<Link to={`/users/${space.owner_id}`} className="font-semibold text-emerald-700 hover:text-emerald-800 underline-offset-2 hover:underline" data-testid="space-host-link">{space.host_name || space.owner_name}</Link>) : (<strong className="text-zinc-700">{space.host_name || space.owner_name}</strong>)}</span>{space.host_avg_rating > 0 && (<span className="flex items-center gap-1 ml-2 text-zinc-700" data-testid="space-host-rating"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /><span className="font-semibold">{space.host_avg_rating}/5</span><span className="text-xs text-zinc-400">({space.host_rating_count} avis)</span></span>)}{space.host_is_superstar && (<Badge className="bg-gradient-to-r from-amber-400 to-amber-500 text-white border-0 gap-1 shadow-sm text-xs" data-testid="host-superstar-badge"><Award className="h-3 w-3" /> Superstar</Badge>)}</div>

            {/* Contact Owner */}
            {user && space.owner_id !== user.id && space.owner_id !== user._id && (
              <div className="mt-4 p-4 rounded-xl bg-zinc-50 border border-zinc-200" data-testid="contact-owner-section">
                <p className="text-sm font-medium text-zinc-700 mb-2 flex items-center gap-2"><MessageCircle className="h-4 w-4" /> Contacter le propriétaire</p>
                <div className="flex gap-2">
                  <Input value={contactMsg} onChange={e => setContactMsg(e.target.value)} placeholder="Votre message..." className="rounded-xl text-sm" data-testid="contact-message-input" />
                  <Button size="sm" disabled={!contactMsg.trim() || sendingContact} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="send-contact-btn"
                    onClick={async () => {
                      if (!contactMsg.trim()) return;
                      setSendingContact(true);
                      try {
                        await api.post("/messages", { receiver_id: space.owner_id, content: contactMsg.trim(), space_id: space.id });
                        setContactMsg(""); toast.success("Message envoyé!");
                      } catch { toast.error("Erreur d'envoi"); }
                      finally { setSendingContact(false); }
                    }}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Amenities */}
          {activeAmenities.length > 0 && (
            <div>
              <h2 className="font-heading text-xl font-bold text-zinc-950 mb-4">Commodites</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {activeAmenities.map(a => (
                  <div key={a.key} className="flex items-center gap-2 p-3 rounded-xl bg-zinc-50 border border-zinc-100" data-testid={`amenity-${a.key}`}>
                    <a.icon className={`h-5 w-5 ${a.color}`} /><span className="text-sm text-zinc-700">{a.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rules */}
          {(rules.pets_allowed !== undefined || rules.campfire_allowed !== undefined || rules.quiet_hours) && (
            <div>
              <h2 className="font-heading text-xl font-bold text-zinc-950 mb-4">Règles</h2>
              <div className="flex flex-wrap gap-3">
                {rules.pets_allowed !== undefined && <Badge variant="outline" className={`rounded-lg ${rules.pets_allowed ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}><PawPrint className="h-3 w-3 mr-1" /> Animaux {rules.pets_allowed ? "acceptes" : "non acceptes"}</Badge>}
                {rules.campfire_allowed !== undefined && <Badge variant="outline" className={`rounded-lg ${rules.campfire_allowed ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}><Flame className="h-3 w-3 mr-1" /> Feu de camp {rules.campfire_allowed ? "permis" : "interdit"}</Badge>}
                {rules.quiet_hours && <Badge variant="outline" className="rounded-lg"><Moon className="h-3 w-3 mr-1" /> Heures calmes: {rules.quiet_hours}</Badge>}
              </div>
            </div>
          )}

          {/* Map */}
          {space.lat && space.lng && (
            <div>
              <h2 className="font-heading text-xl font-bold text-zinc-950 mb-4">Localisation</h2>
              <MapComponent singleMarker={{ lat: space.lat, lng: space.lng, title: space.title, address: `${space.address}, ${space.city}` }} height="300px" />
            </div>
          )}

          {/* Reviews */}
          <div>
            <h2 className="font-heading text-xl font-bold text-zinc-950 mb-4" data-testid="reviews-title">Avis ({reviews.length})</h2>
            {user && canReview.can_review && (
              <Card className="border border-zinc-200 rounded-2xl mb-6" data-testid="review-form">
                <CardContent className="p-4 space-y-3">
                  <p className="text-sm font-medium text-zinc-700">Laisser un avis sur ce terrain</p>
                  <p className="text-xs text-zinc-500">Votre séjour est terminé - partagez votre expérience</p>
                  <StarRating rating={newRating} onRate={setNewRating} interactive size="h-6 w-6" />
                  <Textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Votre commentaire..." className="rounded-xl min-h-[80px]" data-testid="review-comment-input" />
                  <Button onClick={handleReview} disabled={submittingReview || newRating === 0} className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl" data-testid="submit-review-btn"><Send className="h-4 w-4 mr-2" /> Publier</Button>
                </CardContent>
              </Card>
            )}
            {user && !canReview.can_review && canReview.already_reviewed && (
              <p className="text-xs text-emerald-600 mb-4 flex items-center gap-1" data-testid="already-reviewed-msg"><CheckCircle2 className="h-3.5 w-3.5" /> Vous avez déjà laissé un avis pour ce terrain.</p>
            )}
            {user && !canReview.can_review && !canReview.already_reviewed && !canReview.has_completed_stay && (
              <p className="text-xs text-zinc-400 mb-4" data-testid="review-locked-msg">Vous pourrez laisser un avis sur ce terrain une fois votre séjour terminé.</p>
            )}
            {reviews.length === 0 ? <p className="text-zinc-400 text-sm">Aucun avis</p> : (
              <div className="space-y-3">{reviews.map((r, i) => (
                <Card key={r.id} className="border border-zinc-200 rounded-xl" data-testid={`review-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2"><div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center text-sm font-bold text-emerald-700">{r.user_name?.charAt(0)?.toUpperCase()}</div><span className="font-medium text-zinc-900 text-sm">{r.user_name}</span></div>
                      <StarRating rating={r.rating} size="h-4 w-4" />
                    </div>
                    {r.comment && <p className="text-sm text-zinc-600">{r.comment}</p>}
                    <p className="text-xs text-zinc-400 mt-2">{format(new Date(r.created_at), "d MMM yyyy", { locale: fr })}</p>
                  </CardContent>
                </Card>
              ))}</div>
            )}
          </div>
        </div>

        {/* Booking Card */}
        <div>
          <Card className="border border-zinc-200 rounded-2xl shadow-sm sticky top-20" data-testid="booking-card">
            <CardHeader><CardTitle className="font-heading text-xl">Demander cette réservation</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-xs text-emerald-800 flex gap-2">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>Le propriétaire examinera votre profil puis acceptera ou refusera. Paiement uniquement après acceptation.</span>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Dates du séjour</label>
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  disabled={calendarDisabled}
                  modifiers={calendarModifiers}
                  modifiersStyles={calendarModifiersStyles}
                  numberOfMonths={1}
                  className="rounded-xl border border-zinc-200"
                  data-testid="booking-calendar"
                />
                {(blockedDateObjs.length > 0 || bookedDateObjs.length > 0) && (
                  <div className="flex flex-wrap gap-3 text-xs text-zinc-600 mt-2" data-testid="calendar-legend">
                    {blockedDateObjs.length > 0 && (
                      <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-red-200 inline-block" /> Bloque par l&apos;hôte</span>
                    )}
                    {bookedDateObjs.length > 0 && (
                      <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-orange-200 inline-block" /> Déjà réservé</span>
                    )}
                  </div>
                )}
                {dateRange?.from && dateRange?.to && (
                  <p className="text-sm text-zinc-600 mt-2">{format(dateRange.from, "d MMM", { locale: fr })} - {format(dateRange.to, "d MMM yyyy", { locale: fr })} ({nights} nuit{nights > 1 ? "s" : ""})</p>
                )}
              </div>
              {(space.max_spots || 1) > 1 && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Emplacements</label>
                  <Input type="number" min={1} max={space.max_spots || 1} value={numSpots} onChange={e => setNumSpots(Math.min(parseInt(e.target.value) || 1, space.max_spots || 1))} className="rounded-xl" data-testid="num-spots-input" />
                </div>
              )}
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Message au propriétaire (optionnel)</label>
                <Textarea value={requestMsg} onChange={e => setRequestMsg(e.target.value)} placeholder="Presentez-vous brievement..." className="rounded-xl min-h-[70px] text-sm" maxLength={400} data-testid="request-message-input" />
              </div>
              {nights > 0 && !space.is_free && (
                <div className="bg-zinc-50 rounded-xl p-4 space-y-2" data-testid="price-summary">
                  <div className="flex justify-between text-sm text-zinc-600"><span>{space.price_per_night?.toFixed(2)} $ x {nights} nuit{nights > 1 ? "s" : ""}{numSpots > 1 ? ` x ${numSpots}` : ""}</span><span>{pricing.base.toFixed(2)} $</span></div>
                  {space.weekly_price && nights >= 7 && <p className="text-xs text-emerald-600">Tarif semaine appliqué</p>}
                  {space.monthly_price && nights >= 28 && <p className="text-xs text-emerald-600">Tarif mensuel appliqué</p>}
                  <Separator />
                  <div className="flex justify-between font-bold text-zinc-950"><span>Total à payer</span><span>{pricing.base.toFixed(2)} $</span></div>
                </div>
              )}
              {nights > 0 && space.is_free && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-zinc-700" data-testid="free-summary">
                  <p className="font-semibold text-amber-700 mb-1">Terrain public gratuit</p>
                  <p>Aucun paiement requis. Votre réservation sera confirmée immédiatement pour {nights} nuit{nights > 1 ? "s" : ""}.</p>
                </div>
              )}
              {nights > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="booking-disclaimer">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-900 leading-relaxed">
                      <strong>{disclaimerTitle}</strong>
                      {disclaimerIntro
                        ? <> — {disclaimerIntro}</>
                        : <> — En cliquant sur &quot;{space.is_free ? "Confirmer ma réservation" : "Envoyer la demande"}&quot;, je reconnais et j&apos;accepte que&nbsp;:</>}
                    </p>
                  </div>
                  <ul className="list-disc list-inside text-[11px] text-amber-900 space-y-1 pl-1">
                    {disclaimerBullets.map((b, i) => renderBullet(b, i))}
                  </ul>
                  <div className="rounded-lg bg-white/70 border border-amber-200 p-2 text-[11px] text-amber-900" data-testid="cancellation-summary">
                    <strong>Politique d&apos;annulation :</strong> {cancellationSummary}{" "}
                    <Link to="/annulation" target="_blank" className="underline hover:text-amber-700" data-testid="cancellation-policy-link">Voir la politique complète</Link>
                  </div>
                  <label className="flex items-start gap-2 pt-1 cursor-pointer">
                    <Checkbox checked={acceptedTerms} onCheckedChange={setAcceptedTerms} className="mt-0.5" data-testid="accept-terms-checkbox" />
                    <span className="text-xs text-zinc-900 leading-relaxed">
                      <strong>{disclaimerAcceptLabel}</strong> <Link to="/cgu" target="_blank" className="underline hover:text-emerald-700">CGU</Link> · <Link to="/confidentialite" target="_blank" className="underline hover:text-emerald-700">Confidentialité</Link>
                    </span>
                  </label>
                </div>
              )}
              {settings?.prelaunch_enabled && user?.role !== "admin" && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="prelaunch-bookings-blocked">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span>Les réservations ouvriront très bientôt. Vous pouvez explorer les terrains et créer votre compte, mais aucune réservation n&apos;est encore possible.</span>
                </div>
              )}
              <Button
                onClick={handleBook}
                disabled={booking || nights <= 0 || !acceptedTerms || (settings?.prelaunch_enabled && user?.role !== "admin")}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-12 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="book-pay-btn"
              >
                <Send className="mr-2 h-4 w-4" />
                {settings?.prelaunch_enabled && user?.role !== "admin"
                  ? "Réservations bientôt disponibles"
                  : (booking ? "Envoi..." : (space.is_free ? "Confirmer ma réservation" : "Envoyer la demande"))}
              </Button>
              <p className="text-xs text-zinc-400 text-center">{space.is_free ? "Terrain public gratuit — pas de paiement." : "Aucun frais caché pour le campeur. Paiement sécurisé via Stripe."}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        targetType="space"
        targetId={space?.id}
        targetLabel={space?.title}
      />
    </div>
  );
}
